import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRefreshState } from '../../../lib/refreshState';
import { FiAlertCircle, FiCheck, FiEdit2, FiMapPin, FiPercent, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { loadTable, getTableData, saveRow, addRow } from '../../db-viewer/data/dataAccess';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { addNotification } from '../../../lib/notifications';
import { confirmDiscardChanges, setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import { getAccountOwnerId } from '../utils/ownerEntities';
import BusinessPickerTiles, { CoaSetupEntity, getCoaSetupEntities } from './BusinessPickerTiles';
import {
  applicableSalesTaxRates, countryIdOf, ownerTaxLocation, rateInEffect, salesTaxRateLabel, taxCodeOf,
} from '../utils/salesTaxApplicable';
import './GLChartOfAccountsTab.css';
import './TaxesMasterTab.css';

// =============================================
// Masters → Taxes.
//   "Taxes by Owner" — for each person / business of these books: where they are (the
//     province drives which taxes Filing offers), the taxes that apply today, and their
//     own sales-tax registrations (acctg_salestax_registration, migration 140).
//   "Tax Rates" — the country rates (acctg_salestax_rate), read-only: the same list
//     for every user, kept by Akountant.
// The province rule is utils/salesTaxApplicable — the one Filing uses.
// =============================================
type Row = Record<string, any>;

interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

type InnerTab = 'owners' | 'rates';
const INNER_TABS: { key: InnerTab; label: string }[] = [
  { key: 'owners', label: 'Taxes by Owner' },
  { key: 'rates', label: 'Tax Rates' },
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const CANADA_ISO2 = 'CA';

function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return Number(s.id || s.user_id) || null;
  } catch { return null; }
}

const today = () => new Date().toISOString().slice(0, 10);
const iso = (v: any) => String(v == null ? '' : v).slice(0, 10);
const active = (r: Row) => Number(r.is_deleted) !== 1;

interface RegForm {
  registration_id: number | null;
  juris: string;              // `${tax_type_id}:${state_id || ''}`
  registration_number: string;
  registered_from: string;
  registered_to: string;
  filing_frequency: string;
  reporting_method: string;
  fiscal_year_end: string;
  collected_coa_id: string;
  paid_coa_id: string;
  notes: string;
}

const blankForm = (): RegForm => ({
  registration_id: null, juris: '', registration_number: '', registered_from: '', registered_to: '',
  filing_frequency: 'quarterly', reporting_method: 'regular', fiscal_year_end: '',
  collected_coa_id: '', paid_coa_id: '', notes: '',
});

const formFromRow = (r: Row): RegForm => ({
  registration_id: Number(r.registration_id),
  juris: `${r.tax_type_id}:${r.state_id ?? ''}`,
  registration_number: r.registration_number || '',
  registered_from: iso(r.registered_from),
  registered_to: iso(r.registered_to),
  filing_frequency: r.filing_frequency || 'quarterly',
  reporting_method: r.reporting_method || 'regular',
  fiscal_year_end: r.fiscal_year_end || '',
  collected_coa_id: r.collected_coa_id != null ? String(r.collected_coa_id) : '',
  paid_coa_id: r.paid_coa_id != null ? String(r.paid_coa_id) : '',
  notes: r.notes || '',
});

const TaxesMasterTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [innerTab, setInnerTab] = useRefreshState<InnerTab>('taxes.tab', 'owners', v => v === 'owners' || v === 'rates');
  const [resetKey, setResetKey] = useState(0);

  // Guard-then-land: switching tabs, or re-clicking the current one, lands on its start.
  const selectInner = async (key: InnerTab) => {
    if (!(await confirmDiscardChanges())) return;
    setInnerTab(key);
    setResetKey(k => k + 1);
  };

  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  return (
    <div className="coa-tab tx-tab">
      <div className="coa-inner-subtabs">
        {INNER_TABS.map(t => (
          <button key={t.key} type="button"
            className={`coa-inner-subtab ${innerTab === t.key ? 'active' : ''}`}
            onClick={() => selectInner(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <React.Fragment key={`${innerTab}-${resetKey}`}>
        {innerTab === 'owners'
          ? <TaxesByOwner viewMode={viewMode} selectedClientId={selectedClientId} />
          : <TaxRatesList />}
      </React.Fragment>
    </div>
  );
};

// =============================================
// Shared reference data
// =============================================
function useTaxReference() {
  const [types, setTypes] = useState<Row[]>([]);
  const [rates, setRates] = useState<Row[]>([]);
  const [states, setStates] = useState<Row[]>([]);
  const [countries, setCountries] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const load = (t: string) => loadTable(t).catch(() => getTableData(t));
      const [ty, ra, st, co] = await Promise.all([
        load('acctg_salestax_type'), load('acctg_salestax_rate'), load('site_mstr_states'), load('site_mstr_countries'),
      ]);
      if (cancelled) return;
      setTypes(ty); setRates(ra); setStates(st); setCountries(co); setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);
  const statesById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of states) m.set(Number(s.state_id), String(s.state_code || '').toUpperCase());
    return m;
  }, [states]);
  return { types, rates, states, countries, statesById, loaded };
}

// =============================================
// Taxes by Owner
// =============================================
const TaxesByOwner: React.FC<{ viewMode: 'owner' | 'clients'; selectedClientId: number | string | null }> = ({ viewMode, selectedClientId }) => {
  const ref = useTaxReference();
  const [users, setUsers] = useState<Row[]>([]);
  const [businesses, setBusinesses] = useState<Row[]>([]);
  const [history, setHistory] = useState<Row[]>([]);
  const [coa, setCoa] = useState<Row[]>([]);
  const [regs, setRegs] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<RegForm | null>(null);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const load = (t: string) => loadTable(t).catch(() => getTableData(t));
      const [u, b, h, c, r] = await Promise.all([
        load('users'), load('user_businesses'), load('user_business_location_history'),
        load('acctg_coa'), load('acctg_salestax_registration'),
      ]);
      if (cancelled) return;
      setUsers(u); setBusinesses(b); setHistory(h); setCoa(c); setRegs(r); setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // The form's dirty flag belongs to it: cleared when the screen goes away.
  useEffect(() => () => setFormDirty(false), []);

  const accountOwnerId = useMemo(() => getAccountOwnerId(users) ?? 0, [users]);
  const entities = useMemo<CoaSetupEntity[]>(
    () => getCoaSetupEntities(viewMode, accountOwnerId, selectedClientId, users, businesses),
    [viewMode, accountOwnerId, selectedClientId, users, businesses]);

  useEffect(() => {
    if (entities.length === 1 && selectedKey == null) setSelectedKey(entities[0].key);
  }, [entities, selectedKey]);

  const selected = useMemo(() => entities.find(e => e.key === selectedKey) || null, [entities, selectedKey]);

  const stateName = useCallback((code: string, countryId: number | null) => {
    const s = ref.states.find(x => String(x.state_code || '').toUpperCase() === code &&
      (countryId == null || Number(x.countries_id) === countryId));
    return s ? String(s.state_name) : code;
  }, [ref.states]);

  const locationOf = useCallback((key: string) => {
    const loc = ownerTaxLocation(key, users, businesses, history, today());
    const countryId = countryIdOf(loc.country, ref.countries);
    return { ...loc, countryId };
  }, [users, businesses, history, ref.countries]);

  const tileMeta = useCallback((e: CoaSetupEntity) => {
    const loc = locationOf(e.key);
    const n = regs.filter(r => active(r) && r.owner_type === e.owner_type && Number(r.owner_id) === e.owner_id).length;
    const where = loc.stateCode || 'No province set';
    return n ? `${where} · ${n} registration${n === 1 ? '' : 's'}` : where;
  }, [locationOf, regs]);

  const selLoc = useMemo(() => (selected ? locationOf(selected.key) : null), [selected, locationOf]);
  const isCanada = useMemo(() => {
    if (!selLoc?.countryId) return false;
    const c = ref.countries.find(x => Number(x.countries_id) === selLoc.countryId);
    return String(c?.iso2 || '').toUpperCase() === CANADA_ISO2;
  }, [selLoc, ref.countries]);

  // The taxes Filing offers this owner today.
  const applicableToday = useMemo(() => {
    if (!selected || !selLoc) return [];
    return applicableSalesTaxRates({
      rates: ref.rates, types: ref.types, statesById: ref.statesById,
      stateCode: selLoc.stateCode, hasOwner: selLoc.hasOwner, onDate: today(),
    }).rates;
  }, [selected, selLoc, ref.rates, ref.types, ref.statesById]);

  const ownerRegs = useMemo(() => {
    if (!selected) return [];
    return regs.filter(r => active(r) && r.owner_type === selected.owner_type && Number(r.owner_id) === selected.owner_id)
      .sort((a, b) => iso(b.registered_from).localeCompare(iso(a.registered_from)));
  }, [regs, selected]);

  const jurisLabel = useCallback((taxTypeId: any, stateId: any) => {
    const code = taxCodeOf(ref.types, taxTypeId);
    const sc = stateId != null && stateId !== '' ? ref.statesById.get(Number(stateId)) || '' : '';
    return sc ? `${code} ${sc}` : code;
  }, [ref.types, ref.statesById]);

  // A tax applying here that has no registration in force today — a soft suggestion only.
  const unregistered = useMemo(() => {
    const t = today();
    const inForce = new Set(ownerRegs
      .filter(r => (!r.registered_from || iso(r.registered_from) <= t) && (!r.registered_to || iso(r.registered_to) >= t))
      .map(r => `${r.tax_type_id}:${r.state_id ?? ''}`));
    const seen = new Set<string>();
    return applicableToday.filter(r => {
      const k = `${r.tax_type_id}:${r.state_id ?? ''}`;
      if (inForce.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [applicableToday, ownerRegs]);

  // Taxes to register for: the owner's country (the ones applying here first), else all.
  const jurisOptions = useMemo<DropdownOption[]>(() => {
    const here = new Set(applicableToday.map(r => `${r.tax_type_id}:${r.state_id ?? ''}`));
    const pool = ref.rates.filter(r => active(r) &&
      (!selLoc?.countryId || r.country_id == null || Number(r.country_id) === selLoc.countryId));
    const seen = new Set<string>();
    const opts: DropdownOption[] = [];
    for (const r of pool) {
      const k = `${r.tax_type_id}:${r.state_id ?? ''}`;
      if (seen.has(k)) continue;
      seen.add(k);
      const t = ref.types.find(x => String(x.tax_type_id) === String(r.tax_type_id));
      opts.push({
        value: k,
        label: `${jurisLabel(r.tax_type_id, r.state_id)} — ${t?.tax_name || ''}`.trim(),
        group: here.has(k) ? 'Applies here' : 'Elsewhere',
        keywords: t?.tax_name || '',
      });
    }
    return opts.sort((a, b) => (a.group === b.group ? a.label.localeCompare(b.label) : a.group === 'Applies here' ? -1 : 1));
  }, [ref.rates, ref.types, applicableToday, selLoc, jurisLabel]);

  // GL accounts of these books (not headers).
  const coaOptions = useMemo<DropdownOption[]>(() => {
    if (!selected) return [];
    return coa
      .filter(a => active(a) && Number(a.is_active) !== 0 && Number(a.is_header) !== 1 &&
        a.owner_type === selected.owner_type && Number(a.owner_id) === selected.owner_id)
      .sort((a, b) => String(a.account_number || '').localeCompare(String(b.account_number || '')))
      .map(a => ({
        value: String(a.coa_id),
        label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
        keywords: [a.alias_names, a.tags].filter(Boolean).join(', '),
      }));
  }, [coa, selected]);
  const coaName = (id: any) => coaOptions.find(o => o.value === String(id))?.label || '';

  const update = (patch: Partial<RegForm>) => {
    setForm(f => (f ? { ...f, ...patch } : f));
    setFormDirty(true);
    setSavedMsg('');
  };

  const openNew = (juris = '') => {
    setError(''); setSavedMsg('');
    setForm({ ...blankForm(), juris });
  };
  const openEdit = async (r: Row) => {
    if (!(await confirmDiscardChanges())) return;
    setError(''); setSavedMsg('');
    setForm(formFromRow(r));
  };
  const closeForm = () => { setForm(null); setFormDirty(false); setError(''); };

  const notifyOwner = (title: string, message: string) => {
    const uid = currentUserId();
    if (!uid || !accountOwnerId || uid === accountOwnerId) return;
    const actor = users.find(u => Number(u.user_id) === uid);
    const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : 'A user';
    addNotification({ account_owner_id: accountOwnerId, type: 'salestax_registration', title, message, actor_user_id: uid, actor_name: actorName });
  };

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!form || !selected) return false;
    const [taxTypeStr, stateStr] = form.juris.split(':');
    if (!taxTypeStr) { setError('Choose which tax this registration is for.'); return false; }
    if (form.registered_from && form.registered_to && form.registered_to < form.registered_from) {
      setError('"Registered until" is before "Registered from".'); return false;
    }
    if (form.fiscal_year_end && !/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(form.fiscal_year_end)) {
      setError('Fiscal year end is month-day, e.g. 12-31.'); return false;
    }
    const rate = ref.rates.find(r => String(r.tax_type_id) === taxTypeStr && String(r.state_id ?? '') === (stateStr || ''));
    const fields: Row = {
      tax_type_id: Number(taxTypeStr),
      state_id: stateStr ? Number(stateStr) : null,
      country_id: rate?.country_id ?? selLoc?.countryId ?? null,
      registration_number: form.registration_number.trim() || null,
      registered_from: form.registered_from || null,
      registered_to: form.registered_to || null,
      filing_frequency: form.filing_frequency,
      reporting_method: form.reporting_method,
      fiscal_year_end: form.filing_frequency !== 'monthly' ? (form.fiscal_year_end || null) : null,
      collected_coa_id: form.collected_coa_id ? Number(form.collected_coa_id) : null,
      paid_coa_id: form.paid_coa_id ? Number(form.paid_coa_id) : null,
      notes: form.notes.trim() || null,
    };
    setSaving(true); setError('');
    try {
      const label = jurisLabel(fields.tax_type_id, fields.state_id);
      if (form.registration_id) {
        const saved = await saveRow('acctg_salestax_registration', form.registration_id, { ...fields, updated_at: new Date().toISOString() }, 'registration_id');
        setRegs(rs => rs.map(r => (Number(r.registration_id) === form.registration_id ? { ...r, ...fields, ...saved } : r)));
        notifyOwner('Tax registration updated', `${label} registration of "${selected.display_name}" was updated.`);
      } else {
        const created = await addRow('acctg_salestax_registration', {
          ...fields,
          account_owner_id: accountOwnerId,
          owner_type: selected.owner_type,
          owner_id: selected.owner_id,
          is_active: 1, is_deleted: 0,
          created_by: currentUserId(),
        }, 'registration_id');
        setRegs(rs => [...rs, created]);
        notifyOwner('Tax registration added', `${label} registration added for "${selected.display_name}".`);
      }
      setSavedMsg(`${label} registration saved.`);
      setForm(null);
      setFormDirty(false);
      return true;
    } catch (e: any) {
      setError(e?.message || 'Could not save the registration.');
      return false;
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selected, ref.rates, selLoc, accountOwnerId, jurisLabel]);

  useEffect(() => {
    if (!form) return;
    registerSaveHandler(handleSave);
    return () => registerSaveHandler(null);
  }, [form, handleSave]);

  const handleRemove = async (r: Row) => {
    const label = jurisLabel(r.tax_type_id, r.state_id);
    if (!window.confirm(`Remove the ${label} registration${r.registration_number ? ` ${r.registration_number}` : ''}?`)) return;
    try {
      await saveRow('acctg_salestax_registration', r.registration_id, { is_deleted: 1, updated_at: new Date().toISOString() }, 'registration_id');
      setRegs(rs => rs.map(x => (x.registration_id === r.registration_id ? { ...x, is_deleted: 1 } : x)));
      if (form?.registration_id === Number(r.registration_id)) closeForm();
      notifyOwner('Tax registration removed', `${label} registration of "${selected?.display_name}" was removed.`);
    } catch (e: any) {
      setError(e?.message || 'Could not remove the registration.');
    }
  };

  const pickEntity = (key: string) => { setSelectedKey(key); setForm(null); setFormDirty(false); setSavedMsg(''); setError(''); };

  if (!loaded || !ref.loaded) return <div className="coa-empty"><p>Loading taxes…</p></div>;

  const simplifiedLabel = isCanada ? 'Quick Method' : 'Simplified / flat-rate';
  const freqLabel = (v: string) => FREQUENCIES.find(f => f.value === v)?.label || v;

  return (
    <div className="tx-owners">
      <div className="coa-info-banner">
        The taxes Filing offers on a receipt come from <strong>where its owner is</strong>: the province's
        own tax plus the federal one (GST), or HST alone in an HST province. Record here the sales-tax
        numbers a person or business is <strong>registered</strong> for — it's optional and never needed to file a document.
      </div>

      {entities.length === 0 ? (
        <div className="coa-empty">
          <p>Nobody found for this {viewMode === 'clients' ? 'client' : 'account'}. Add a person or a business in <strong>Family &amp; Businesses</strong> first.</p>
        </div>
      ) : (
        <>
          {entities.length > 1 && (
            <BusinessPickerTiles entities={entities} selectedKey={selectedKey} onSelect={pickEntity}
              typeName={() => undefined} meta={tileMeta} />
          )}

          {selected && selLoc && (
            <div className="coa-form-card tx-owner-card">
              <div className="tx-owner-head">
                <h3>{selected.display_name}</h3>
                <span className="tx-location">
                  <FiMapPin size={14} />
                  {selLoc.stateCode
                    ? <>{stateName(selLoc.stateCode, selLoc.countryId)} ({selLoc.stateCode}){selLoc.country ? `, ${selLoc.country}` : ''}</>
                    : <span className="tx-warn">No province set — Filing shows the federal tax only. Add the address in <strong>Family &amp; Businesses</strong>.</span>}
                </span>
              </div>

              <div className="tx-section">
                <div className="tx-section-title"><FiPercent size={14} /> Taxes that apply today</div>
                {applicableToday.length === 0 ? (
                  <p className="tx-muted">No sales-tax rates are set up for this location yet.</p>
                ) : (
                  <div className="tx-chips">
                    {applicableToday.map(r => {
                      const t = ref.types.find(x => String(x.tax_type_id) === String(r.tax_type_id));
                      return (
                        <span key={r.tax_rate_id} className="tx-chip" title={t?.tax_name || ''}>
                          {salesTaxRateLabel(r, ref.types, ref.statesById)}
                          <em>{r.state_id != null && r.state_id !== '' ? 'provincial' : 'federal'}</em>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="tx-section">
                <div className="tx-section-title tx-section-title-row">
                  <span>Registrations</span>
                  {!form && (
                    <button type="button" className="coa-btn-secondary tx-btn-sm" onClick={() => openNew()}>
                      <FiPlus size={14} /> Add registration
                    </button>
                  )}
                </div>

                {ownerRegs.length === 0 && !form && (
                  <p className="tx-muted">No sales-tax registrations recorded.</p>
                )}

                {ownerRegs.length > 0 && (
                  <div className="tx-table-wrap">
                    <table className="tx-table">
                      <thead>
                        <tr>
                          <th>Tax</th><th>Number</th><th>Registered</th><th>Files</th><th>Method</th><th>Accounts</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {ownerRegs.map(r => {
                          const ended = r.registered_to && iso(r.registered_to) < today();
                          return (
                            <tr key={r.registration_id} className={ended ? 'tx-row-past' : ''}>
                              <td><strong>{jurisLabel(r.tax_type_id, r.state_id)}</strong></td>
                              <td>{r.registration_number || <span className="tx-muted">—</span>}</td>
                              <td>
                                {iso(r.registered_from) || '…'} → {iso(r.registered_to) || 'now'}
                                {ended && <span className="tx-badge">ended</span>}
                              </td>
                              <td>{freqLabel(r.filing_frequency)}{r.filing_frequency !== 'monthly' && r.fiscal_year_end ? ` (year end ${r.fiscal_year_end})` : ''}</td>
                              <td>{r.reporting_method === 'simplified' ? simplifiedLabel : 'Regular'}</td>
                              <td className="tx-accts">
                                {r.collected_coa_id ? <div>Charged: {coaName(r.collected_coa_id) || `#${r.collected_coa_id}`}</div> : null}
                                {r.paid_coa_id ? <div>Paid: {coaName(r.paid_coa_id) || `#${r.paid_coa_id}`}</div> : null}
                                {!r.collected_coa_id && !r.paid_coa_id && <span className="tx-muted">—</span>}
                              </td>
                              <td className="tx-row-actions">
                                <button type="button" className="coa-btn-icon" title="Edit" aria-label="Edit registration" onClick={() => openEdit(r)}><FiEdit2 size={14} /></button>
                                <button type="button" className="coa-btn-icon" title="Remove" aria-label="Remove registration" onClick={() => handleRemove(r)}><FiTrash2 size={14} /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!form && unregistered.length > 0 && (
                  <div className="tx-suggest">
                    Registered for {unregistered.map(r => jurisLabel(r.tax_type_id, r.state_id)).join(' / ')}?{' '}
                    {unregistered.map(r => (
                      <button key={r.tax_rate_id} type="button" className="tx-link"
                        onClick={() => openNew(`${r.tax_type_id}:${r.state_id ?? ''}`)}>
                        Add {jurisLabel(r.tax_type_id, r.state_id)} number
                      </button>
                    ))}
                  </div>
                )}

                {form && (
                  <div className="tx-form">
                    <div className="coa-field-row">
                      <div className="coa-field-group">
                        <label className="coa-field-label">Tax <span className="coa-required">*</span></label>
                        <SearchableDropdown options={jurisOptions} value={form.juris}
                          onChange={(v: string) => update({ juris: v })} placeholder="-- Which tax --" />
                      </div>
                      <div className="coa-field-group">
                        <label className="coa-field-label">Registration number</label>
                        <div className="tx-input-wrap">
                          <input className="coa-search-input" type="text" maxLength={50} value={form.registration_number}
                            placeholder={isCanada ? 'e.g. 123456789 RT0001' : 'Registration / VAT number'}
                            onChange={e => update({ registration_number: e.target.value })} />
                          {form.registration_number && (
                            <button type="button" className="tx-clear" aria-label="Clear" onClick={() => update({ registration_number: '' })}><FiX size={13} /></button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="coa-field-row">
                      <div className="coa-field-group">
                        <label className="coa-field-label">Registered from</label>
                        <input className="coa-search-input" type="date" value={form.registered_from}
                          onChange={e => update({ registered_from: e.target.value })} />
                      </div>
                      <div className="coa-field-group">
                        <label className="coa-field-label">Registered until (blank = still registered)</label>
                        <input className="coa-search-input" type="date" value={form.registered_to}
                          onChange={e => update({ registered_to: e.target.value })} />
                      </div>
                    </div>

                    <div className="coa-field-row">
                      <div className="coa-field-group">
                        <label className="coa-field-label">Files a return</label>
                        <select className="coa-search-input" value={form.filing_frequency}
                          onChange={e => update({ filing_frequency: e.target.value })}>
                          {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      </div>
                      {form.filing_frequency !== 'monthly' && (
                        <div className="coa-field-group">
                          <label className="coa-field-label">Fiscal year end (MM-DD, blank = 12-31)</label>
                          <input className="coa-search-input" type="text" maxLength={5} placeholder="12-31" value={form.fiscal_year_end}
                            onChange={e => update({ fiscal_year_end: e.target.value })} />
                        </div>
                      )}
                      <div className="coa-field-group">
                        <label className="coa-field-label">Reporting method</label>
                        <select className="coa-search-input" value={form.reporting_method}
                          onChange={e => update({ reporting_method: e.target.value })}>
                          <option value="regular">Regular (tax charged minus tax paid)</option>
                          <option value="simplified">{simplifiedLabel}</option>
                        </select>
                      </div>
                    </div>

                    <div className="coa-field-row">
                      <div className="coa-field-group">
                        <label className="coa-field-label">Account for tax charged on sales</label>
                        <SearchableDropdown options={coaOptions} value={form.collected_coa_id}
                          onChange={(v: string) => update({ collected_coa_id: v })}
                          placeholder={coaOptions.length ? '-- e.g. Sales tax payable --' : 'No GL accounts set up for these books'} />
                      </div>
                      <div className="coa-field-group">
                        <label className="coa-field-label">Account for tax paid on purchases</label>
                        <SearchableDropdown options={coaOptions} value={form.paid_coa_id}
                          onChange={(v: string) => update({ paid_coa_id: v })}
                          placeholder={coaOptions.length ? '-- e.g. Sales tax recoverable --' : 'No GL accounts set up for these books'} />
                      </div>
                    </div>

                    <div className="coa-field-group">
                      <label className="coa-field-label">Notes</label>
                      <textarea className="coa-search-input tx-notes" rows={2} value={form.notes}
                        onChange={e => update({ notes: e.target.value })} />
                    </div>

                    {error && <div className="coa-field-error"><FiAlertCircle size={14} /> {error}</div>}

                    <div className="coa-action-bar">
                      <button type="button" className="coa-btn-primary" disabled={saving} onClick={handleSave}>
                        <FiCheck size={15} /> {saving ? 'Saving…' : form.registration_id ? 'Update registration' : 'Save registration'}
                      </button>
                      <button type="button" className="coa-btn-secondary" onClick={closeForm}>Cancel</button>
                    </div>
                  </div>
                )}
                {!form && error && <div className="coa-field-error"><FiAlertCircle size={14} /> {error}</div>}
                {savedMsg && <span className="coa-saved-msg">{savedMsg}</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =============================================
// Tax Rates (read-only reference)
// =============================================
const TaxRatesList: React.FC = () => {
  const ref = useTaxReference();
  const [countryId, setCountryId] = useState('');
  const [stateFilter, setStateFilter] = useState('');   // '' all · 'federal' · state_id
  const [showPast, setShowPast] = useState(false);

  // Countries that have rates; default to the first (Canada today).
  const countryOptions = useMemo(() => {
    const ids = Array.from(new Set(ref.rates.filter(active).map(r => r.country_id).filter((v: any) => v != null).map(Number)));
    return ids.map(id => ({ id, name: ref.countries.find(c => Number(c.countries_id) === id)?.name || `Country #${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [ref.rates, ref.countries]);
  useEffect(() => {
    if (!countryId && countryOptions.length) setCountryId(String(countryOptions[0].id));
  }, [countryOptions, countryId]);

  const t = today();
  const status = (r: Row) => (rateInEffect(r, t) ? 'current' : iso(r.effective_from) > t ? 'future' : 'past');

  const rows = useMemo(() => ref.rates
    .filter(active)
    .filter(r => !countryId || Number(r.country_id) === Number(countryId))
    .filter(r => !stateFilter || (stateFilter === 'federal' ? (r.state_id == null || r.state_id === '') : String(r.state_id) === stateFilter))
    .filter(r => showPast || status(r) !== 'past')
    .sort((a, b) => {
      const sa = a.state_id == null ? '' : ref.statesById.get(Number(a.state_id)) || '';
      const sb = b.state_id == null ? '' : ref.statesById.get(Number(b.state_id)) || '';
      return sa.localeCompare(sb) || iso(b.effective_from).localeCompare(iso(a.effective_from));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [ref.rates, ref.statesById, countryId, stateFilter, showPast]);

  const provinceOptions = useMemo(() => {
    const ids = Array.from(new Set(ref.rates.filter(r => active(r) && Number(r.country_id) === Number(countryId) && r.state_id != null)
      .map(r => Number(r.state_id))));
    return ids.map(id => {
      const s = ref.states.find(x => Number(x.state_id) === id);
      return { id, label: s ? `${s.state_name} (${s.state_code})` : `#${id}` };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [ref.rates, ref.states, countryId]);

  if (!ref.loaded) return <div className="coa-empty"><p>Loading tax rates…</p></div>;

  return (
    <div className="tx-rates">
      <div className="coa-info-banner">
        Sales-tax rates are kept up to date by Akountant and are the same for every user. Each rate
        applies from its start date; when a rate changes, receipts dated before the change keep the old rate.
      </div>
      <div className="tx-filters">
        <select className="coa-search-input" value={countryId} aria-label="Country"
          onChange={e => { setCountryId(e.target.value); setStateFilter(''); }}>
          {countryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="coa-search-input" value={stateFilter} aria-label="Province / state"
          onChange={e => setStateFilter(e.target.value)}>
          <option value="">All provinces / states</option>
          <option value="federal">Country-wide (federal)</option>
          {provinceOptions.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <label className="tx-check">
          <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} /> Show past rates
        </label>
      </div>
      {rows.length === 0 ? (
        <div className="coa-empty"><p>No rates for this selection.</p></div>
      ) : (
        <div className="tx-table-wrap">
          <table className="tx-table">
            <thead>
              <tr><th>Tax</th><th>Name</th><th>Applies in</th><th className="tx-num">Rate</th><th>From</th><th>Until</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const ty = ref.types.find(x => String(x.tax_type_id) === String(r.tax_type_id));
                const st = r.state_id != null ? ref.states.find(x => Number(x.state_id) === Number(r.state_id)) : null;
                const s = status(r);
                return (
                  <tr key={r.tax_rate_id} className={s === 'past' ? 'tx-row-past' : ''}>
                    <td><strong>{ty?.tax_code || ''}</strong></td>
                    <td>{ty?.tax_name || ''}</td>
                    <td>{st ? `${st.state_name} (${st.state_code})` : 'Country-wide'}</td>
                    <td className="tx-num">{Number(r.rate_percent)}%</td>
                    <td>{iso(r.effective_from) || '—'}</td>
                    <td>{iso(r.effective_to) || '—'}</td>
                    <td><span className={`tx-status tx-status-${s}`}>{s}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaxesMasterTab;
